import {
  type FormSearchMultipleSelectionProps,
  type OptionSelectionProps,
  FormSearchMultipleSelection,
  mergeSx,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import {
  useGetAppRolesLazyQuery,
  OrderType,
  type AppRole,
  type AppRoleSearchResult,
} from '@user-management/graphql';
import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';

interface UserAppRoleSelectionProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<FormSearchMultipleSelectionProps<TFieldValues>, 'options'> {
  helperText?: React.ReactNode;
  valueKey?: 'id' | 'name';
}

export default function UserAppRoleSelection<TFieldValues extends FieldValues = FieldValues>(
  props: Readonly<UserAppRoleSelectionProps<TFieldValues>>,
) {
  const { name, helperText, label, placeholder, valueKey = 'name', ...restProps } = props;
  const [mappedAppRoles, setMappedAppRoles] = useState<OptionSelectionProps[]>([]);

  const [isUserAppRoleSelectionOpen, userAppRoleSelectionHandlers] = useDisclosure(false);
  const { open: openUserAppRoleSelection, close: closeUserAppRoleSelection } =
    userAppRoleSelectionHandlers;

  const [getAppRoles, { loading: isAppRoleLoading }] = useGetAppRolesLazyQuery({
    variables: {
      paging: { pageSize: 10000, currentPage: 0 }, // NOTE: get all = 10000
      order: { field: 'name', type: OrderType.Asc },
      search: {},
    },
    onCompleted: (res) => {
      const appRolesResult = (res?.getAppRoles ?? {}) as AppRoleSearchResult;
      const mappedAppRoles = appRolesResult.roles.map((role: AppRole) => {
        return { label: role.name, value: valueKey === 'id' ? role.id : role.name };
      });
      setMappedAppRoles(mappedAppRoles);
    },
  });

  const handleOpenOptions = () => {
    getAppRoles();
    openUserAppRoleSelection();
  };

  const noOptionsText = helperText ?? 'ไม่พบข้อมูล';

  return (
    <FormSearchMultipleSelection
      name={name}
      options={mappedAppRoles}
      placeholder={placeholder}
      loading={isAppRoleLoading}
      noOptionsText={noOptionsText}
      label={label}
      open={isUserAppRoleSelectionOpen}
      onOpen={handleOpenOptions}
      onClose={closeUserAppRoleSelection}
      {...restProps}
      sx={mergeSx({ width: '100%' }, restProps.sx)}
      getOptionLabel={(option) => option?.label ?? ''}
    />
  );
}
